import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type FormEvent, type ReactNode } from "react";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import { COMMENT_NOTE_LIMIT, type MessageComment, type PreviewComment } from "../api/contract";
import type { CommentNotice, CommentsController } from "../hooks/useComments";
import { usePopoverPlacement } from "../hooks/usePopoverPlacement";
import { useKeepInView } from "../hooks/useKeepInView";
import { dockCorner, type DockCorner } from "../lib/popover";
import { LaneNote } from "./LaneNote";
import { Spinner } from "./Spinner";

/** How a comment stands, in a few words, for the pin's label and its card. */
export function commentStatus(comment: Pick<PreviewComment, "state" | "resultNote">, moved = false): string {
  if (moved && (comment.state === "draft" || comment.state === "sent")) return "That part of the page has moved or been removed";
  switch (comment.state) {
    case "draft": return "Not sent yet";
    case "sent": return "With Stax";
    case "done": return "Done";
    case "asked": return "Stax asked about it in the chat";
    case "failed": return comment.resultNote ? `Couldn’t: ${comment.resultNote}` : "Couldn’t do this one";
  }
}

export function pinLabel(comment: Pick<PreviewComment, "number" | "note" | "state" | "resultNote">, moved = false): string {
  const end = (text: string) => {
    const trimmed = text.trim();
    return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
  };
  return `Comment ${comment.number}: ${end(comment.note)} ${end(commentStatus(comment, moved))}`;
}

interface CommentCardProps {
  /** Where the pin sits within the preview area. */
  anchor: { top: number; bottom: number; left: number };
  /** The saved comment, or none while a new one is being written. */
  comment: PreviewComment | null;
  moved?: boolean;
  saving: boolean;
  error: string | null;
  onSave: (note: string) => void;
  onClose: () => void;
  /** Removes a comment not yet sent, or dismisses a resolved one. */
  onRemove?: () => void;
  initialNote?: string;
  /** Point to change: what the new comment is about, with the levels above selectable instead. */
  trail?: ReactNode;
  /** What was selected, for the question: "What should change in Hand-building?". */
  about?: string;
  /**
   * Sits in a corner of the preview, the one away from where the owner tapped, so they can keep
   * tapping into what's selected.
   */
  docked?: boolean;
  /** A list item's own fields, to change exactly; the note to Stax follows as the alternative. */
  fields?: ReactNode;
}

/**
 * A comment where its pin sits: a small card beside it on wide screens, a sheet at the bottom on
 * phones (the same placement as the text editor). New and unsent comments can be written and
 * changed; a sent one shows how it went.
 */
export function CommentCard({ anchor, comment, moved = false, saving, error, onSave, onClose, onRemove, initialNote = "", trail, about, docked = false, fields }: CommentCardProps) {
  const editable = !comment || comment.state === "draft";
  const [note, setNote] = useState(comment?.note ?? initialNote);
  const field = useRef<HTMLTextAreaElement>(null);
  const closer = useRef<HTMLButtonElement>(null);
  const card = useRef<HTMLFormElement>(null);
  const placement = usePopoverPlacement(card, anchor.left - 24, docked ? undefined : anchor);
  const view = useKeepInView(card, `${anchor.top}:${anchor.bottom}:${placement.above}:${docked}:${comment?.id ?? "new"}`);
  const [corner, setCorner] = useState<DockCorner>("bottom-left");
  useLayoutEffect(() => {
    const area = card.current?.closest<HTMLElement>(".bw-frame");
    if (docked && area) setCorner(dockCorner(anchor, { width: area.clientWidth, height: area.clientHeight }));
  }, [docked, anchor]);
  const commentId = comment?.id ?? null;
  useEffect(() => {
    setNote(comment?.note ?? initialNote);
    // With fields to change, nothing takes focus: on a tablet that would open the keyboard over them.
    if (editable && !fields) field.current?.focus({ preventScroll: true });
    else closer.current?.focus({ preventScroll: true });
    // Only a different comment resets what is being typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commentId]);

  const trimmed = note.trim();
  const changed = trimmed !== (comment?.note ?? "").trim();
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (editable && trimmed && changed && !saving) onSave(trimmed);
  };
  const title = comment ? `Comment ${comment.number}` : "New comment";
  const resolved = comment && (comment.state === "done" || comment.state === "failed" || comment.state === "asked");

  return (
    <form
      ref={card}
      className="bw-text-editor bw-comment-card"
      data-tether={docked ? "none" : placement.above ? "above" : "below"}
      data-docked={docked ? corner : undefined}
      style={{ top: placement.above ? anchor.top - 10 : Math.max(8, anchor.bottom + 10), transform: view.transform(placement.above ? "translateY(-100%)" : undefined), ...view.style, left: placement.left, "--bw-tether": `${placement.tether}px` } as CSSProperties}
      onSubmit={submit}
      aria-labelledby="bw-comment-title"
      data-state={comment?.state ?? "new"}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.stopPropagation();
          onClose();
        }
        if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) submit(event);
      }}
    >
      <div className="bw-text-editor-head">
        {trail ?? <strong id="bw-comment-title">{title}</strong>}
        <button ref={closer} type="button" className="bw-btn bw-btn--icon" onClick={onClose} aria-label={editable && changed ? "Close without saving" : "Close"}>
          <X size={18} aria-hidden="true" />
        </button>
      </div>
      {fields}
      {editable ? (
        <label>
          <span className={about ? "bw-comment-question" : "bw-sr"}>{fields ? "Or tell Stax" : about ? `What should change in ${about}?` : "What should change here?"}</span>
          <textarea
            ref={field}
            rows={3}
            value={note}
            maxLength={COMMENT_NOTE_LIMIT}
            placeholder={about ? "Tell Stax, like you’d tell your web designer" : "What should change here?"}
            onChange={(event) => setNote(event.target.value)}
          />
        </label>
      ) : (
        <p className="bw-comment-note">{comment!.note}</p>
      )}
      {comment && (comment.state !== "draft" || moved) && (
        <p className="bw-item-note" data-state={comment.state} role="status">{commentStatus(comment, moved)}</p>
      )}
      {error && <p className="bw-item-note" role="alert">{error}</p>}
      <div className="bw-actions bw-text-editor-actions">
        {!comment && <button type="button" className="bw-btn bw-btn--quiet" onClick={onClose}>Cancel</button>}
        {onRemove && (comment?.state === "draft" || resolved) && (
          <button type="button" className="bw-btn bw-btn--quiet" onClick={onRemove}>{resolved ? "Dismiss" : "Remove"}</button>
        )}
        {editable && (
          <button type="submit" className="bw-btn bw-btn--primary" disabled={!trimmed || !changed || saving}>
            {saving && <Spinner />}
            {saving ? "Saving…" : comment ? "Save" : about ? "Add note" : "Add comment"}
          </button>
        )}
      </div>
      {editable && <LaneNote lane="requested" />}
    </form>
  );
}

const NOTICES: Record<CommentNotice, string> = {
  moved: "Part of the page a comment points at has moved or been removed. Remove it, or add a new one where it should go.",
  busy: "Stax is still working on your last request. Send your comments when it’s done.",
  limited: "You’ve reached this hour’s limit for requests. Your comments are kept here, so try again later.",
  failed: "Your comments couldn’t be sent. They’re kept here, so try again.",
  unsaved: "That comment couldn’t be saved. Try again.",
  full: "You have as many open comments as you can. Send or remove some first.",
};

interface CommentTrayProps {
  comments: CommentsController;
  /** Titles of the site's pages, by path, for comments on another page. */
  pageTitles: Record<string, string>;
  currentPath: string;
  /** A message can go now (the admin isn't busy with one). */
  canSend: boolean;
  /** Comment placement mode; leaving it opens the batch for review. */
  placing: boolean;
  onShow: (comment: PreviewComment) => void;
  /** Full list in the Advanced Comments panel; compact review tray above Simple chat. */
  variant?: "compact" | "full";
}

/**
 * The comments waiting to be sent, at the top of the preview: "3 comments", one batch send, and a
 * list to find or remove each pin before sending. Resolved pins can be cleared here.
 */
export function CommentTray({ comments, pageTitles, currentPath, canSend, placing, onShow, variant = "compact" }: CommentTrayProps) {
  const [open, setOpen] = useState(variant === "full");
  const wasPlacing = useRef(placing);
  const drafts = comments.drafts;
  const sendable = drafts.filter((comment) => !comments.moved.has(comment.id));
  const resolved = comments.comments.filter((comment) => comment.state !== "draft");
  const visibleResolved = variant === "full" ? resolved : resolved.filter((comment) => comment.state === "sent");
  const sending = comments.busy === "sending";
  useEffect(() => {
    if (wasPlacing.current && !placing && drafts.length > 0) setOpen(true);
    wasPlacing.current = placing;
  }, [placing, drafts.length]);
  if (drafts.length === 0 && visibleResolved.length === 0 && !comments.notice) return null;
  const count = drafts.length === 1 ? "1 comment" : `${drafts.length} comments`;
  const where = (comment: PreviewComment) => comments.moved.has(comment.id)
    ? "Moved or removed"
    : variant === "full" ? `On ${pageTitles[comment.pagePath] ?? comment.pagePath}` : comment.pagePath === currentPath ? null : `On ${pageTitles[comment.pagePath] ?? comment.pagePath}`;

  return (
    <div className="bw-tray" data-variant={variant} role="region" aria-label="Your comments">
      <div className="bw-tray-bar">
        {variant === "compact" && <button type="button" className="bw-btn bw-btn--quiet bw-tray-toggle" aria-expanded={open} aria-controls="bw-tray-list" onClick={() => setOpen((now) => !now)}>
          {drafts.length > 0 ? count : visibleResolved.length === 1 ? "1 comment with Stax" : `${visibleResolved.length} comments with Stax`}
          {open ? <ChevronUp size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
        </button>}
        {/* While Stax is busy, the bar says when sending opens instead of showing a button that can't work. */}
        {drafts.length > 0 && !canSend && !sending && !comments.notice && <p className="bw-tray-hint" role="status">Send when Stax is done</p>}
        {drafts.length > 0 && (canSend || sending) && (
          <button type="button" className="bw-btn bw-btn--primary" disabled={sending || sendable.length === 0} onClick={() => void comments.send(sendable.map((comment) => comment.id))}>
            {sending && <Spinner />}
            {sending ? "Sending…" : variant === "full" ? `Send ${sendable.length} comment${sendable.length === 1 ? "" : "s"}` : drafts.length === 1 ? "Send comment" : `Send all ${sendable.length}`}
          </button>
        )}
      </div>
      {comments.notice && (
        <p className="bw-tray-notice" role="alert">
          <span>{NOTICES[comments.notice]}</span>
          <button type="button" className="bw-btn bw-btn--icon" aria-label="Dismiss" onClick={comments.dismissNotice}><X size={16} aria-hidden="true" /></button>
        </p>
      )}
      {open && (
        <div className="bw-tray-list" id="bw-tray-list">
          {drafts.length > 0 && (
            <ol aria-label="Not sent yet">
              {drafts.map((comment) => (
                <li key={comment.id} data-moved={comments.moved.has(comment.id) || undefined}>
                  <span className="bw-pin-chip" data-state="draft" aria-hidden="true">{comment.number}</span>
                  <button type="button" className="bw-tray-note" onClick={() => { setOpen(false); onShow(comment); }}>
                    <span>{comment.note}</span>
                    {where(comment) && <small>{where(comment)}</small>}
                  </button>
                  {variant === "full" && <button type="button" className="bw-btn bw-btn--text bw-tray-show" onClick={() => onShow(comment)}>Show me</button>}
                  <button type="button" className="bw-btn bw-btn--icon" aria-label={`Remove comment ${comment.number}`} disabled={comments.busy !== null} onClick={() => void comments.remove(comment.id)}>
                    <X size={16} aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ol>
          )}
          {visibleResolved.length > 0 && (
            <>
              <p className="bw-tray-heading">Sent, done or answered</p>
              <ol aria-label="Sent, done or answered">
                {visibleResolved.map((comment) => (
                  <li key={comment.id} data-resolved="">
                    <span className="bw-pin-chip" data-state={comment.state} aria-hidden="true">{comment.number}</span>
                  <button type="button" className="bw-tray-note" onClick={() => { setOpen(false); onShow(comment); }}>
                      <span>{comment.note}</span>
                      <small>{commentStatus(comment)}</small>
                  </button>
                  {variant === "full" && <button type="button" className="bw-btn bw-btn--text bw-tray-show" onClick={() => onShow(comment)}>Show me</button>}
                  <button type="button" className="bw-btn bw-btn--icon" aria-label={`Dismiss comment ${comment.number}`} disabled={comments.busy !== null} onClick={() => void comments.remove(comment.id)}>
                      <X size={16} aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ol>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** The numbered chips on the owner's message that sent comments; each leads back to its pin. */
export function CommentChips({ comments, onShow }: { comments: MessageComment[]; onShow?: (comment: MessageComment) => void }) {
  return (
    <span className="bw-msg-comments">
      <span className="bw-msg-comments-head">{comments.length === 1 ? "You sent a comment" : `You sent ${comments.length} comments`}</span>
      {comments.map((comment) => (
        <span key={comment.id} className="bw-msg-comment">
          <button type="button" className="bw-pin-chip" onClick={() => onShow?.(comment)} aria-label={`Show comment ${comment.number} on the page`}>{comment.number}</button>
          <span>{comment.note}</span>
        </span>
      ))}
    </span>
  );
}
